import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { sendOTP, verifyOTP } from '../actions/userActions'
import '../assests/glass-theme.css'
import { ByPokerLauncher, FullLogo, ThemeToggle } from '../ui'

// ─── Inline SVG Icons ────────────────────────────────────────────
const PhoneIcon = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" strokeLinecap="round" strokeWidth="2.5" />
  </svg>
)

const AlertIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" strokeWidth="2.5" />
  </svg>
)

const BackIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
)

// ─── OTP timer hook ───────────────────────────────────────────────
const useCountdown = (initial = 30) => {
  const [seconds, setSeconds] = useState(0)
  const [active, setActive] = useState(false)

  const start = () => {
    setSeconds(initial)
    setActive(true)
  }

  useEffect(() => {
    if (!active) return
    if (seconds <= 0) { setActive(false); return }
    const t = setTimeout(() => setSeconds(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [seconds, active])

  return { seconds, active, start }
}

const OTP_LENGTH = 4
const emptyOtpState = () => Array.from({ length: OTP_LENGTH }, () => '')

// ─── Main Component ────────────────────────────────────────────────
const PlayerLogin = () => {
  const dispatch  = useDispatch()
  const navigate  = useNavigate()

  // Step 1 = phone,  Step 2 = OTP
  const [step, setStep]             = useState(1)
  const [phone, setPhone]           = useState('')
  const [otp, setOtp]               = useState(emptyOtpState)
  const [loading, setLoading]       = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [otpError, setOtpError]     = useState('')
  const [agreed, setAgreed]         = useState(false)
  const [otpMeta, setOtpMeta]       = useState({ mode: 'dynamic', defaultOtp: '', helpText: '' })

  const otpRefs = useRef([])
  const { seconds: countdown, start: startCountdown } = useCountdown(30)

  // Retrieve pending club from session (set by ClubWelcomePage)
  const pendingClub = (() => {
    try { return JSON.parse(sessionStorage.getItem('pendingWelcomeClub') || 'null') }
    catch { return null }
  })()

  // ── Helpers ──────────────────────────────────────────────────────
  const cleanPhone = phone.replace(/\D/g, '').slice(-10)
  const otpValue = otp.join('')

  const applyOtpValue = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, OTP_LENGTH)
    const next = emptyOtpState()

    digits.split('').forEach((digit, index) => {
      next[index] = digit
    })

    setOtp(next)
    setOtpError('')

    const nextIndex = digits.length >= OTP_LENGTH ? OTP_LENGTH - 1 : digits.length
    otpRefs.current[Math.max(0, nextIndex)]?.focus()
  }

  const validatePhone = () => {
    if (!cleanPhone || cleanPhone.length !== 10 || !/^[6-9]/.test(cleanPhone)) {
      setPhoneError('Please enter a valid 10-digit mobile number.')
      return false
    }
    if (!agreed) {
      setPhoneError('Please agree to the Terms & Conditions to continue.')
      return false
    }
    setPhoneError('')
    return true
  }

  // ── Step 1: Send OTP ──────────────────────────────────────────────
  const handleSendOTP = async () => {
    if (!validatePhone()) return
    setLoading(true)
    try {
      const data = await dispatch(sendOTP(cleanPhone))
      setOtpMeta({
        mode: data?.otpMode || 'dynamic',
        defaultOtp: data?.defaultOtp || '',
        helpText: data?.helpText || '',
      })
      startCountdown()
      setStep(2)
      setOtp(emptyOtpState())
      setOtpError('')
    } catch (err) {
      const msg = err?.message || 'Failed to send OTP. Please try again.'
      setPhoneError(msg)
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2: Verify OTP ────────────────────────────────────────────
  const handleVerifyOTP = async () => {
    if (otpValue.length !== OTP_LENGTH) {
      setOtpError('Please enter the complete 4-digit OTP.')
      return
    }
    setLoading(true)
    try {
      const data = await dispatch(verifyOTP(cleanPhone, otpValue))

      // Persist user session
      if (data?.token) {
        localStorage.setItem('userInfo', JSON.stringify(data))
        localStorage.setItem('token', data.token)

        // Transfer club context flag
        if (pendingClub) sessionStorage.setItem('showWelcomePopup', 'true')
      }

      if (data?.requiresVerification) {
        localStorage.setItem('tempUserInfo', JSON.stringify(data))
        toast.info(data?.message || 'Please complete verification to continue.', {
          autoClose: 2000,
          hideProgressBar: true,
        })
        navigate(`/verification?step=${data?.verificationStep || 'aadhaar'}`)
        return
      }

      if (!data?.token) {
        setOtpError(data?.message || 'Login could not be completed. Please try again.')
        return
      }

      toast.success('Login successful!', { autoClose: 1500, hideProgressBar: true })
      navigate(data?.redirectUrl || '/dashboard')
    } catch (err) {
      const msg = err?.message || 'Invalid OTP. Please try again.'
      setOtpError(msg)
    } finally {
      setLoading(false)
    }
  }

  // ── OTP input management ─────────────────────────────────────────
  const handleOtpChange = (idx, val) => {
    const digits = val.replace(/\D/g, '')
    if (digits.length > 1) {
      applyOtpValue(digits)
      return
    }

    const digit = digits.slice(-1)
    const next  = [...otp]
    next[idx] = digit
    setOtp(next)
    setOtpError('')
    if (digit && idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus()
  }

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    applyOtpValue(pasted)
  }

  // ── Resend OTP ────────────────────────────────────────────────────
  const handleResend = async () => {
    if (countdown > 0) return
    setLoading(true)
    try {
      const data = await dispatch(sendOTP(cleanPhone))
      setOtpMeta({
        mode: data?.otpMode || 'dynamic',
        defaultOtp: data?.defaultOtp || '',
        helpText: data?.helpText || '',
      })
      startCountdown()
      setOtp(emptyOtpState())
      setOtpError('')
      otpRefs.current[0]?.focus()
    } catch (err) {
      toast.error('Could not resend OTP. Try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (step !== 2) return undefined

    otpRefs.current[0]?.focus()

    if (
      typeof window === 'undefined' ||
      !('OTPCredential' in window) ||
      !navigator.credentials?.get
    ) {
      return undefined
    }

    const abortController = new AbortController()

    navigator.credentials.get({
      otp: { transport: ['sms'] },
      signal: abortController.signal,
    })
      .then((credential) => {
        if (credential?.code) {
          applyOtpValue(credential.code)
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.debug('Web OTP unavailable:', error)
        }
      })

    return () => abortController.abort()
  }, [step])

  return (
    <div className="bg-scene-dark" style={styles.root}>
      <div aria-hidden="true" style={styles.orb1} />
      <div aria-hidden="true" style={styles.orb2} />

      <header style={styles.topBar}>
        {step === 2 ? (
          <button style={styles.backBtn} onClick={() => setStep(1)} aria-label="Go back">
            <BackIcon />
            <span style={styles.backLabel}>Back</span>
          </button>
        ) : (
          <span />
        )}
        <ThemeToggle />
      </header>

      <main style={styles.main}>
        <div className="gl-anim-in" style={{ marginBottom: 20 }}>
          <FullLogo width={step === 1 ? 190 : 140} />
        </div>

        {pendingClub && step === 1 && (
          <div style={styles.clubBanner} className="gl-anim-in">
            <span style={styles.clubBannerText}>Joining <strong>{pendingClub.name}</strong></span>
          </div>
        )}

        <div className="glass-dark gl-anim-up" style={styles.card}>
          {step === 1 ? (
            <PhoneStep
              phone={phone}
              setPhone={setPhone}
              phoneError={phoneError}
              agreed={agreed}
              setAgreed={setAgreed}
              loading={loading}
              onSubmit={handleSendOTP}
            />
          ) : (
            <OTPStep
              phone={cleanPhone}
              otp={otp}
              otpMeta={otpMeta}
              otpRefs={otpRefs}
              otpError={otpError}
              loading={loading}
              countdown={countdown}
              onOtpChange={handleOtpChange}
              onOtpKeyDown={handleOtpKeyDown}
              onOtpPaste={handleOtpPaste}
              onSubmit={handleVerifyOTP}
              onResend={handleResend}
            />
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <ByPokerLauncher height={22} />
        <span className="footer-disclaimer">
          This game may be habit forming and financially risky. Play responsibly.
        </span>
      </footer>
    </div>
  )
}


// ─── Phone Step ────────────────────────────────────────────────────
const PhoneStep = ({ phone, setPhone, phoneError, agreed, setAgreed, loading, onSubmit }) => (
  <>
    <h1 style={styles.cardTitle}>
      We'll need your<br />mobile number
    </h1>
    <p style={styles.cardSubtitle}>Join the table and start playing today.</p>

    <div style={styles.fieldGroup}>
      <label style={styles.label}>Please enter your mobile number</label>

      <div style={styles.inputWrapper}>
        <input
          className="glass-input"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder="Enter your number"
          maxLength={10}
          value={phone}
          onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
          onKeyDown={e => e.key === 'Enter' && onSubmit()}
          style={styles.phoneInput}
        />
        <span style={styles.phoneIcon}><PhoneIcon /></span>
      </div>

      {phoneError && (
        <div className="gl-error-text">
          <AlertIcon /> {phoneError}
        </div>
      )}
    </div>

    <label style={styles.checkboxRow}>
      <input
        className="gl-checkbox"
        type="checkbox"
        checked={agreed}
        onChange={e => setAgreed(e.target.checked)}
      />
      <span style={styles.checkboxLabel}>
        I am 18 years or above and agree to these&nbsp;
        <a href="#terms" style={styles.linkText}>Terms &amp; Conditions</a>
        &nbsp;and&nbsp;
        <a href="#privacy" style={styles.linkText}>Privacy Policy</a>
      </span>
    </label>

    <button
      className="btn-gold"
      onClick={onSubmit}
      disabled={loading}
      style={{ marginTop: 24 }}
    >
      {loading ? 'Sending OTP…' : 'PROCEED'}
    </button>
  </>
)

// ─── OTP Step ──────────────────────────────────────────────────────
const OTPStep = ({
  phone, otp, otpMeta, otpRefs, otpError, loading,
  countdown, onOtpChange, onOtpKeyDown, onOtpPaste,
  onSubmit, onResend,
}) => (
  <>
    <h1 style={styles.cardTitle}>
      Please enter your<br />secure OTP
    </h1>

    <div style={styles.fieldGroup}>
      <label style={styles.label}>4-Digit OTP</label>

      <div style={styles.otpRow} onPaste={onOtpPaste}>
        {otp.map((digit, idx) => (
          <input
            key={idx}
            className="otp-input"
            ref={el => (otpRefs.current[idx] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={idx === 0 ? 'one-time-code' : 'off'}
            name={idx === 0 ? 'one-time-code' : `otp-digit-${idx + 1}`}
            maxLength={idx === 0 ? OTP_LENGTH : 1}
            value={digit}
            onChange={e => onOtpChange(idx, e.target.value)}
            onKeyDown={e => onOtpKeyDown(idx, e)}
            aria-label={`OTP digit ${idx + 1}`}
            autoFocus={idx === 0}
          />
        ))}
      </div>

      {otpError && (
        <div className="gl-error-text" style={{ justifyContent: 'center', marginTop: 10 }}>
          <AlertIcon /> {otpError}
        </div>
      )}
    </div>

    <p style={styles.sentTo}>
      {otpMeta?.mode === 'static' ? 'Logging in for' : "We've sent it to"}{' '}
      <strong style={{ color: '#fff' }}>+91 {phone}</strong>
    </p>

    {otpMeta?.mode === 'static' ? (
      <div style={styles.staticOtpNotice}>
        <span>{otpMeta?.helpText || 'OTP delivery is temporarily unavailable. Use the default 4-digit OTP to continue.'}</span>
        <strong style={styles.staticOtpCode}>{otpMeta?.defaultOtp || '0000'}</strong>
      </div>
    ) : (
      <>
        <p style={styles.resendRow}>
          Didn't get it? Don't worry request another one in&nbsp;
          {countdown > 0 ? (
            <span style={styles.countdown}>{countdown}s</span>
          ) : (
            <button style={styles.resendBtn} onClick={onResend} disabled={loading}>
              Request Another OTP
            </button>
          )}
        </p>

        {countdown === 0 && !loading && (
          <p style={styles.resendHint}>Request Another OTP</p>
        )}
      </>
    )}

    <button
      className="btn-gold"
      onClick={onSubmit}
      disabled={loading || otp.join('').length !== OTP_LENGTH}
      style={{ marginTop: 28 }}
    >
      {loading ? 'Verifying…' : 'PROCEED'}
    </button>
  </>
)

// ─── helpers ──────────────────────────────────────────────────────
// ─── Styles ───────────────────────────────────────────────────────
const muted = 'var(--cm-text-muted)'

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    paddingTop: 'max(env(safe-area-inset-top), 0px)',
  },
  orb1: {
    position: 'absolute',
    top: '-140px',
    right: '-100px',
    width: '360px',
    height: '360px',
    borderRadius: '50%',
    background: 'radial-gradient(closest-side, var(--cm-red-soft), transparent)',
    pointerEvents: 'none',
  },
  orb2: {
    position: 'absolute',
    bottom: '-120px',
    left: '-90px',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    border: '18px dashed var(--cm-red)',
    opacity: 0.08,
    pointerEvents: 'none',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px 0',
    position: 'relative',
    zIndex: 2,
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    color: 'var(--cm-text)',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 15,
    fontWeight: 600,
  },
  backLabel: { fontSize: 15, color: 'var(--cm-text)' },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 20px 24px',
    position: 'relative',
    zIndex: 2,
  },
  clubBanner: {
    marginBottom: 16,
    padding: '8px 16px',
    borderRadius: 999,
    background: 'var(--cm-gold-soft)',
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 13,
    color: 'var(--cm-text)',
  },
  clubBannerText: { fontFamily: 'var(--cm-font-sans)' },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    fontFamily: 'var(--cm-font-display)',
    fontSize: 24,
    fontWeight: 800,
    color: 'var(--cm-text)',
    lineHeight: 1.25,
    marginBottom: 6,
  },
  cardSubtitle: {
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 14,
    color: muted,
    marginBottom: 22,
  },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 8 },
  label: {
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--cm-text)',
    marginBottom: 8,
  },
  inputWrapper: { position: 'relative' },
  phoneInput: { paddingRight: 46 },
  phoneIcon: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: muted,
    pointerEvents: 'none',
    display: 'flex',
  },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 18, cursor: 'pointer' },
  checkboxLabel: {
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 12.5,
    color: muted,
    lineHeight: 1.5,
    userSelect: 'none',
  },
  linkText: { color: 'var(--cm-gold-text)', textDecoration: 'underline', cursor: 'pointer' },
  otpRow: { display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4, marginBottom: 8, flexWrap: 'wrap' },
  sentTo: {
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 13,
    color: muted,
    textAlign: 'center',
    marginTop: 18,
  },
  staticOtpNotice: {
    marginTop: 14,
    padding: '12px 14px',
    borderRadius: 12,
    background: 'var(--cm-gold-soft)',
    color: 'var(--cm-text)',
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 12.5,
    lineHeight: 1.55,
    textAlign: 'center',
    display: 'grid',
    gap: 8,
  },
  staticOtpCode: {
    display: 'inline-block',
    fontFamily: 'var(--cm-font-display)',
    fontSize: 28,
    letterSpacing: 6,
    color: 'var(--cm-gold-text)',
  },
  resendRow: {
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 12.5,
    color: muted,
    textAlign: 'center',
    marginTop: 10,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 4,
    alignItems: 'center',
  },
  countdown: { color: 'var(--cm-gold-text)', fontWeight: 700, fontSize: 13 },
  resendBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--cm-text-faint)',
    cursor: 'not-allowed',
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 12.5,
    padding: 0,
    textDecoration: 'underline',
  },
  resendHint: {
    fontFamily: 'var(--cm-font-sans)',
    fontSize: 13,
    color: 'var(--cm-text-faint)',
    textAlign: 'center',
    marginTop: 6,
    cursor: 'pointer',
  },
  footer: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    paddingTop: 8,
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
  },
}

export default PlayerLogin
